import { Accordion, Skeleton, Typography } from "@mui/material";
import { styled } from "@mui/system";
import { ObjectId } from "bson";
import React from "react";
import ReadableDateString from "../../../lib/ReadableDateString";
import { BlogInterface } from "../../../models/blogs";

const BlogCard = styled("div", { shouldForwardProp: (prop) => prop !== "source" })<{ source?: string }>(
	({ theme, source }) => ({
		display: "flex",
		flexDirection: "column",
		alignItems: "flex-start",
		justifyContent: "flex-start",
		gap: "1rem",
		width: "20rem",
		// [theme.breakpoints.down("md")]: {
		// 	width: "calc(50% - 0.67rem)",
		// },
		// [theme.breakpoints.down("sm")]: {
		// 	width: source === "carousel" ? "10rem" : "100%",
		// 	height: "22.5rem",
		// },
		height: "26.5rem",
		borderRadius: "1rem",
		cursor: "pointer",
		backgroundColor: theme.palette.background.paper,
		"&:hover": {
			transform: "scale(1.02)",
			transition: "transform 0.3s",
			// boxShadow: theme.shadows[2],
		},
		"&:not(hover)": {
			transform: "scale(1.00)",
			transition: "transform 0.3s",
			// boxShadow: theme.shadows[0],
		},
	}),
);

const BlogCardImage = styled("img")(({ theme }) => ({
	width: "100%",
	minHeight: "12rem",
	maxHeight: "12rem",
	objectFit: "cover",
	borderRadius: "1rem",
}));

const BlogCardContentContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	alignItems: "flex-start",
	justifyContent: "center",
	gap: "1rem",
	width: "100%",
	padding: "0rem 1rem 1rem 1rem",
	borderRadius: "1rem",
}));

const BlogCardTitle = styled(Typography)(({ theme }) => ({
	fontSize: "1rem",
	lineHeight: "1.5rem",
	fontWeight: 400,
	color: theme.palette.text.primary,
	// minHeight: "3rem",
	maxHeight: "3rem",
	overflow: "hidden",
	width: "100%",
	textAlign: "left",
	textDecoration: "none",
	"&:hover": {
		textDecoration: "underline",
	},
})) as typeof Typography;

const BlogCardDescription = styled(Typography)(({ theme }) => ({
	fontSize: "1rem",
	lineHeight: "1.5rem",
	fontWeight: 400,
	color: theme.palette.text.secondary,
	// minHeight: "4.5rem",
	maxHeight: "4.5rem",
	overflow: "hidden",
	width: "100%",
	textAlign: "left",
	textDecoration: "none",
	"&:hover": {
		textDecoration: "underline",
	},
})) as typeof Typography;

const BlogCardAuthorName = styled(Typography)(({ theme }) => ({
	fontSize: "1rem",
	lineHeight: "1.5rem",
	fontWeight: 400,
	color: theme.palette.text.primary,
	textDecoration: "none",
	"&:hover": {
		textDecoration: "underline",
	},
})) as typeof Typography;

const BlogIndicatorsContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	alignItems: "center",
	justifyContent: "flex-start",
	gap: "0.5rem",
	width: "100%",
}));

const BlogIndicatorCreatedAt = styled(Typography)(({ theme }) => ({
	fontSize: "0.75rem",
	lineHeight: "1.125rem",
	fontWeight: 400,
	color: theme.palette.text.secondary,
})) as typeof Typography;

const BlogIndicatorReadTime = styled(Typography)(({ theme }) => ({
	fontSize: "0.75rem",
	lineHeight: "1.125rem",
	fontWeight: 400,
	color: theme.palette.primary.main,
})) as typeof Typography;

const BlogPersonaContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	alignItems: "center",
	justifyContent: "flex-start",
	gap: "0.5rem",
	width: "100%",
}));

const BlogPersonaProfilePicture = styled("img")(({ theme }) => ({
	width: "2.25rem",
	height: "2.25rem",
	borderRadius: "50%",
})) as any;

const handleBlogCardClick = (author: string, slug: string) => {
	window.location.href =
		(process.env.PRODUCTION_URL || "") +
		"loading.html?callbackUrl=" +
		encodeURIComponent("blog/" + author + "/" + slug);
};

const AccordionSection = styled(Accordion)(({ theme }) => ({}));

const Text = styled(Typography)(({ theme }) => ({
	fontWeight: 600,
	lineHeight: "2rem",
}));

const BlogCardComponent = ({ blogId, source }: { blogId: ObjectId; source?:string }) => {
	const [blogData, setBlogData] = React.useState<BlogInterface | null>(null);
	const [loading, setLoading] = React.useState<boolean>(true);
	React.useEffect(() => {
		const fetchBlogData = async () => {
			const blog_data: BlogInterface | null = await (
				await fetch((process.env.PRODUCTION_API_URL || "") + "blog/" + blogId)
			).json();
			setBlogData(blog_data);
			setLoading(false);
		};
		fetchBlogData();
		// eslint-disable-next-line
	}, []);

	return (
		<React.Fragment>
			{!loading && blogData !== null ? (
				<BlogCard
					onClick={(event: any) => {
						event.preventDefault();
						if (
							!["blog-card-persona-container", "blog-card-author-profile-picture", "blog-card-author-name"].includes(
								event.target.id.toString(),
							)
						) {
							handleBlogCardClick(
								blogData.author.first_name.toLowerCase() + "-" + blogData.author.last_name.toLowerCase(),
								blogData.slug,
							);
						}
					}}
					source={source}
				>
					<BlogCardImage
						src={(process.env.PRODUCTION_API_URL || "") + "blog/cover-image/" + blogData.cover_image_file_id.toString()}
						alt={blogData.cover_image_alt_text}
						loading="lazy"
					/>
					<BlogCardContentContainer>
						<BlogCardTitle
							variant="body1"
							component="h3"
						>
							{blogData.title}
						</BlogCardTitle>
						<BlogCardDescription
							variant="body1"
							component="p"
						>
							{blogData.meta_description.length > 43
								? blogData.meta_description.substring(0, 43) + "..."
								: blogData.meta_description}
						</BlogCardDescription>
						<BlogPersonaContainer
							id="blog-card-persona-container"
							onClick={(event: any) => {
								event.preventDefault();
								handleBlogCardClick(
									blogData.author.first_name.toLowerCase() + "-" + blogData.author.last_name.toLowerCase(),
									"",
								);
							}}
						>
							<BlogPersonaProfilePicture
								id="blog-card-author-profile-picture"
								src={
									(process.env.PRODUCTION_API_URL || "") +
									"blog/persona-profile-picture/" +
									(blogData.author.persona_id as any).profile_picture_file_id.toString()
								}
								alt={blogData.author.first_name + " " + blogData.author.last_name}
								loading="lazy"
							/>
							<BlogCardAuthorName
								id="blog-card-author-name"
								variant="body1"
								component="p"
							>
								{blogData.author.first_name + " " + blogData.author.last_name}
							</BlogCardAuthorName>
						</BlogPersonaContainer>
						<BlogIndicatorsContainer>
							<BlogIndicatorCreatedAt
								variant="body1"
								component="p"
							>
								{ReadableDateString(new Date(blogData.created_at), "half")}
							</BlogIndicatorCreatedAt>
							&bull;
							<BlogIndicatorReadTime
								variant="body1"
								component="p"
							>
								{blogData.read_time_in_minutes.toString() + " min read"}
							</BlogIndicatorReadTime>
						</BlogIndicatorsContainer>
					</BlogCardContentContainer>
				</BlogCard>
			) : (
				<BlogCard>
					<Skeleton
						variant="rectangular"
						width="100%"
						height="16rem"
						style={{ borderRadius: "1rem" }}
					/>
					<BlogCardContentContainer>
						<Skeleton
							variant="text"
							width="100%"
							height="3rem"
						/>
						<Skeleton
							variant="text"
							width="100%"
							height="4.5rem"
						/>
						<Skeleton
							variant="text"
							width="100%"
							height="1.5rem"
						/>
						<Skeleton
							variant="text"
							width="100%"
							height="1.5rem"
						/>
					</BlogCardContentContainer>
				</BlogCard>
			)}
		</React.Fragment>
	);
};

export default BlogCardComponent;
